import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import { Connection } from "~/stores/slices/database";
import ConnectionTypeModal from "~/components/connections/connection-type-modal";
import DeleteConnectionModal from "~/components/connections/delete-connection-modal";
import ConnectionCard from "~/components/connections/connection-card";
import { FaPlus, FaDatabase } from "react-icons/fa";
import { RiServerLine } from "react-icons/ri";

const Connections = () => {
  const navigate = useNavigate();
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] =
    useState<Connection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { connectionsList, getConnectionsList, deleteConnection } =
    useGlobalStore((state) => state);

  useEffect(() => {
    getConnectionsList();
  }, [getConnectionsList]);

  const handleAddConnection = () => {
    setShowTypeModal(true);
  };

  const handleEditConnection = (connection: Connection) => {
    navigate(`/connections/${connection._id}/edit`);
  };

  const handleDeleteConnection = async () => {
    if (connectionToDelete) {
      setIsDeleting(true);
      try {
        await deleteConnection(connectionToDelete._id);
        setConnectionToDelete(null);
        setDeleteModalOpen(false);
      } catch (error) {
        console.error("Failed to delete connection:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const confirmDelete = (connection: Connection) => {
    setConnectionToDelete(connection);
    setDeleteModalOpen(true);
  };

  return (
    <div className="h-full bg-base-200/50 overflow-y-auto">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
                <RiServerLine className="w-6 h-6" />
                Connections
              </h1>
              <p className="text-sm text-base-content/60">
                Manage your database and service connections
              </p>
            </div>
            <button
              onClick={handleAddConnection}
              className="btn btn-primary btn-sm gap-2"
            >
              <FaPlus className="w-3 h-3" />
              Add Connection
            </button>
          </div>
        </div>

        {/* Connections List */}
        {connectionsList.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {connectionsList.map((connection: Connection) => (
              <ConnectionCard
                key={connection._id}
                connection={connection}
                handleEditConnection={handleEditConnection}
                confirmDelete={confirmDelete}
              />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="card bg-base-100 shadow-sm border border-base-200">
            <div className="card-body flex flex-col items-center justify-center py-16">
              <div className="p-4 bg-base-200/50 rounded-full mb-4">
                <FaDatabase className="w-10 h-10 text-base-content/40" />
              </div>
              <h2 className="text-xl font-semibold mb-2">No connections yet</h2>
              <p className="text-sm text-base-content/60 text-center max-w-sm mb-6">
                Add your first database connection to start syncing and
                analyzing your data.
              </p>
              <button
                onClick={handleAddConnection}
                className="btn btn-primary btn-sm gap-2"
              >
                <FaPlus className="w-3 h-3" />
                Add Your First Connection
              </button>
            </div>
          </div>
        )}

        {/* Connection Type Modal */}
        <ConnectionTypeModal
          open={showTypeModal}
          onClose={() => setShowTypeModal(false)}
        />

        {/* Delete Confirmation Modal */}
        {connectionToDelete && (
          <DeleteConnectionModal
            isOpen={deleteModalOpen}
            connectionName={connectionToDelete.name}
            onConfirm={handleDeleteConnection}
            onCancel={() => {
              setDeleteModalOpen(false);
              setConnectionToDelete(null);
            }}
            isDeleting={isDeleting}
          />
        )}
      </div>
    </div>
  );
};

export default Connections;
